import { TeamRole, getPermissions } from "@alch/dx-entities";
import type { TRPCRouter } from "@alch/dx-webapp-api";
import useSearchParam, { SearchParam } from "@hooks/useSearchParam.js";
import type { inferRouterOutputs } from "@trpc/server";
import { IS_PRODUCTION } from "@util/deployEnv";
import { trpc } from "@util/trpc/trpcClient";

const queryOptions = {
  refetchOnWindowFocus: false,
};

const stringToTeamRole: Record<string, TeamRole> = {
  admin: TeamRole.Admin,
  developer: TeamRole.Developer,
  viewer: TeamRole.Viewer,
};

type CurrentUser = inferRouterOutputs<TRPCRouter>["users"]["getUser"];

const createCurrentUserSelect = (testTeamRoleParam: string) => {
  return (user: CurrentUser) => {
    const testTeamRole = stringToTeamRole[testTeamRoleParam];

    if (!testTeamRole) {
      return user;
    }

    if (IS_PRODUCTION && !user.isStaff) {
      return user;
    }

    return {
      ...user,
      isBillingAdmin: testTeamRole === TeamRole.Admin,
      teamRole: testTeamRole,
      permissions: getPermissions(testTeamRole),
    };
  };
};

const useCurrentUser = () => {
  const testTeamRoleParam = useSearchParam(SearchParam.TestTeamRole) || "";

  return trpc.users.getUser.useQuery(undefined, {
    ...queryOptions,
    select: createCurrentUserSelect(testTeamRoleParam),
  });
};

export function useSuspenseCurrentUser() {
  const testTeamRoleParam = useSearchParam(SearchParam.TestTeamRole) || "";

  return trpc.users.getUser.useSuspenseQuery(undefined, {
    ...queryOptions,
    select: createCurrentUserSelect(testTeamRoleParam),
  });
}

export default useCurrentUser;
